import { useEffect, useRef, useState } from "react";

import {
  Loader2,
  X,
  Check,
  AlertTriangle,
  Square,
  ImageIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import {
  usePreviewTestStore,
  runStatus,
  overallStatus,
  type PreviewTestStep,
  type PreviewTestRun,
  type PreviewTestVerdict,
} from "@/stores/preview-test-store";
import type { PreviewTestStepStatus } from "@optidev/shared-types";

interface PreviewTestWindowProps {
  /** Cancel the active agent turn — wired to the chat's normal stop handler. */
  onStopRun: () => void;
  /** Scopes persisted runs to this project (clears them when switching). */
  projectId?: string;
}

const DOT: Record<PreviewTestVerdict, string> = {
  running: "bg-blue-500",
  passed: "bg-emerald-500",
  issues: "bg-amber-500",
  error: "bg-red-500",
  stopped: "bg-muted-foreground/60",
};

interface BannerTint {
  bg: string;
  border: string;
  iconBg: string;
  fg: string;
  Icon: React.ComponentType<{ className?: string }>;
  spin?: boolean;
}

function bannerTint(status: PreviewTestVerdict): BannerTint {
  switch (status) {
    case "stopped":
      return {
        bg: "bg-muted/60",
        border: "border-border",
        iconBg: "bg-muted-foreground/10",
        fg: "text-muted-foreground",
        Icon: Square,
      };
    case "running":
      return {
        bg: "bg-blue-50/70 dark:bg-blue-950/20",
        border: "border-blue-200/60 dark:border-blue-900/40",
        iconBg: "bg-blue-500/15",
        fg: "text-blue-600 dark:text-blue-400",
        Icon: Loader2,
        spin: true,
      };
    case "issues":
      return {
        bg: "bg-amber-50/70 dark:bg-amber-950/20",
        border: "border-amber-200/60 dark:border-amber-900/40",
        iconBg: "bg-amber-500/15",
        fg: "text-amber-600 dark:text-amber-400",
        Icon: AlertTriangle,
      };
    case "error":
      return {
        bg: "bg-red-50/70 dark:bg-red-950/20",
        border: "border-red-200/60 dark:border-red-900/40",
        iconBg: "bg-red-500/15",
        fg: "text-red-600 dark:text-red-400",
        Icon: X,
      };
    case "passed":
    default:
      return {
        bg: "bg-emerald-50/70 dark:bg-emerald-950/20",
        border: "border-emerald-200/60 dark:border-emerald-900/40",
        iconBg: "bg-emerald-500/15",
        fg: "text-emerald-600 dark:text-emerald-400",
        Icon: Check,
      };
  }
}

/**
 * Headline-level rollup. The user only acts on running/stopped — passed,
 * issues, and errors all mean "the agent finished, you don't have to do
 * anything", and the agent typically self-corrects intermediate errors. So the
 * banner/headline/tint collapses all three "done" outcomes into one. Per-run
 * history chips still use the raw verdict so distinct runs stay visually
 * distinguishable for navigation.
 */
function headlineVerdict(v: PreviewTestVerdict): PreviewTestVerdict {
  return v === "error" || v === "issues" ? "passed" : v;
}

/** Small timeline node that sits on the rail (ring masks the line behind it). */
function Node({ status }: { status: PreviewTestStepStatus }) {
  const base =
    "relative z-10 mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ring-4 ring-background";
  switch (status) {
    case "running":
      return (
        <span className={cn(base, "bg-blue-500/15")}>
          <Loader2 className="h-2.5 w-2.5 animate-spin text-blue-500" />
        </span>
      );
    case "issues":
      return (
        <span className={cn(base, "bg-amber-500/15")}>
          <AlertTriangle className="h-2.5 w-2.5 text-amber-500" />
        </span>
      );
    case "error":
      return (
        <span className={cn(base, "bg-red-500/15")}>
          <X className="h-2.5 w-2.5 text-red-500" />
        </span>
      );
    case "passed":
    default:
      return (
        <span className={cn(base, "bg-emerald-500/15")}>
          <Check className="h-2.5 w-2.5 text-emerald-500" />
        </span>
      );
  }
}

function relativeTime(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 10) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

/**
 * Headline title — the app's CURRENT state across all runs. Outcome buckets
 * (passed/issues/error) collapse to one "Tests completed" line: intermediate
 * tool errors typically self-correct, and the user has no separate action to
 * take for issues vs passed at the headline level. Step-level statuses inside
 * the expanded window still surface what actually happened.
 */
function overallTitle(runs: PreviewTestRun[]): string {
  switch (headlineVerdict(overallStatus(runs))) {
    case "running":
      return "Running tests…";
    case "stopped":
      return "Test run stopped";
    default:
      return "Tests completed";
  }
}

/** Secondary detail line for the headline (current state across all runs). */
function overallSummary(runs: PreviewTestRun[]): string {
  const verdict = headlineVerdict(overallStatus(runs));
  if (verdict === "running") {
    const latest = runs[runs.length - 1];
    const done = latest.steps.filter((s) => s.status !== "running").length;
    return `Running · ${done}/${latest.steps.length}`;
  }
  if (verdict === "stopped") return "Stopped before finishing";
  const n = runs[runs.length - 1].steps.length;
  return `${n} step${n === 1 ? "" : "s"}`;
}

function StepRow({ step, isLast }: { step: PreviewTestStep; isLast: boolean }) {
  const [imgBroken, setImgBroken] = useState(false);
  const screenshotSrc = step.screenshotBase64
    ? `data:${step.screenshotMimeType || "image/jpeg"};base64,${step.screenshotBase64}`
    : null;
  const showImage = !!screenshotSrc && !imgBroken;

  const route = step.route || "/";
  // Quiet subtitle: orientation always, route only when it's a real path.
  const metaLine = [route !== "/" ? route : null, step.orientation]
    .filter(Boolean)
    .join(" · ");

  const showIssues =
    step.kind === "check" && step.status === "issues" && !!step.issues?.length;
  const showTap = step.kind === "tap" && !!step.tapSteps?.length;
  const showError = step.status === "error" && !!step.summary;
  const showScreenshotNote = !showImage && !!step.screenshotCaptured;

  return (
    <div className="flex gap-3">
      {/* Rail: node + connector down to the next node */}
      <div className="flex flex-col items-center">
        <Node status={step.status} />
        {!isLast && <div className="w-px flex-1 bg-border" />}
      </div>

      {/* Content */}
      <div className={cn("min-w-0 flex-1", isLast ? "pb-1" : "pb-5")}>
        <div className="truncate text-[13px] font-medium leading-snug text-foreground">
          {step.label}
        </div>
        {metaLine && (
          <div className="mt-0.5 text-[11px] text-muted-foreground/55">{metaLine}</div>
        )}

        {showError && (
          <div className="mt-1.5 rounded-md bg-red-500/5 px-2 py-1.5">
            <p className="break-words text-[11px] leading-relaxed text-red-600/90 dark:text-red-400/90">
              {step.summary}
            </p>
          </div>
        )}

        {showIssues && step.issues && (
          <p className="mt-1.5 text-[11px] leading-relaxed">
            {step.issues.map((iss, i) => (
              <span key={iss.category}>
                {i > 0 && <span className="text-muted-foreground/40"> · </span>}
                <span className="text-foreground/75">{iss.category}</span>
                <span className="text-muted-foreground"> ×{iss.count}</span>
              </span>
            ))}
          </p>
        )}

        {showTap && step.tapSteps && (
          <div className="mt-1.5 space-y-1">
            {step.tapSteps.map((tap, i) => (
              <div
                key={`${tap.label}-${i}`}
                className="flex items-center gap-1.5 text-[11px]"
              >
                {tap.ok ? (
                  <Check className="h-3 w-3 shrink-0 text-emerald-500/80" />
                ) : (
                  <X className="h-3 w-3 shrink-0 text-red-500/80" />
                )}
                <span className="text-foreground/80">{tap.label}</span>
                {tap.detail && (
                  <span className="truncate text-muted-foreground/60">{tap.detail}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {showImage && screenshotSrc && (
          <div className="mt-2 inline-block max-w-full overflow-hidden rounded-md border border-border bg-muted/30">
            <img
              src={screenshotSrc}
              alt={`${step.label} screenshot`}
              className="block max-h-44 w-auto max-w-full object-contain"
              onError={() => setImgBroken(true)}
            />
          </div>
        )}

        {showScreenshotNote && (
          <span className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ImageIcon className="h-3.5 w-3.5" />
            Screenshot captured
          </span>
        )}
      </div>
    </div>
  );
}

export function PreviewTestWindow({ onStopRun, projectId }: PreviewTestWindowProps) {
  const runs = usePreviewTestStore((s) => s.runs);
  const isOpen = usePreviewTestStore((s) => s.isOpen);
  const dismissed = usePreviewTestStore((s) => s.dismissed);
  const viewedTaskId = usePreviewTestStore((s) => s.viewedTaskId);
  const open = usePreviewTestStore((s) => s.open);
  const close = usePreviewTestStore((s) => s.close);
  const dismiss = usePreviewTestStore((s) => s.dismiss);
  const setProject = usePreviewTestStore((s) => s.setProject);

  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (projectId) setProject(projectId);
  }, [projectId, setProject]);

  const latestRun = runs.length ? runs[runs.length - 1] : null;
  const viewedRun = runs.find((r) => r.taskId === viewedTaskId) ?? latestRun;

  useEffect(() => {
    if (!isOpen) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [viewedRun?.steps, isOpen]);

  if (runs.length === 0 || !latestRun) return null;

  // ---- Collapsed banner — a wide, rounded-bottom "U" tab that drops in from
  // under the chat/visual toolbar, tinted by the run's status. ----
  if (!isOpen) {
    // Dismissed → the banner is gone; the "Tests" reopen tab (above the chat
    // input, see PreviewTestReopenTab) brings it back.
    if (dismissed) return null;
    const status = headlineVerdict(overallStatus(runs));
    const tint = bannerTint(status);
    const BannerIcon = tint.Icon;

    return (
      <div className="shrink-0 animate-in slide-in-from-top-2 fade-in px-3 pt-2 duration-300">
        <div className="group relative rounded-xl border border-border bg-card shadow-sm transition-colors hover:border-foreground/15 hover:bg-foreground/[0.04] dark:hover:bg-foreground/[0.06]">
          {/* The whole card opens the Test Window */}
          <button
            type="button"
            onClick={() => open(latestRun.taskId)}
            className="flex w-full items-center gap-2.5 rounded-xl py-2 pl-2 pr-9 text-left"
            title="Open Test Run"
          >
            <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full", tint.iconBg)}>
              <BannerIcon className={cn("h-4 w-4", tint.fg, tint.spin && "animate-spin")} />
            </span>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="text-[13px] font-semibold text-foreground">
                {overallTitle(runs)}
              </div>
              <div className="truncate text-[11px] text-muted-foreground">
                {overallSummary(runs)} · {relativeTime(latestRun.startedAt)}
              </div>
            </div>
          </button>

          {/* Dismiss — revealed on hover, sits above the card */}
          <button
            type="button"
            onClick={dismiss}
            className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground opacity-0 transition hover:bg-foreground/10 hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // ---- Expanded window — covers the chat column ----
  if (!viewedRun) return null;
  // Headline reflects the app's CURRENT state across all runs, not the viewed
  // historical run — so a resolved issue never reads as "to fix". Flattened to
  // a single "Tests completed" outcome so transient tool errors don't stick.
  const overall = headlineVerdict(overallStatus(runs));

  return (
    <div className="absolute inset-0 z-20 flex animate-in fade-in flex-col bg-background duration-150">
      {/* Header */}
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-4">
        <span className={cn("h-2 w-2 shrink-0 rounded-full", DOT[overall], overall === "running" && "animate-pulse")} />
        <span className="text-[13px] font-medium text-foreground">{overallTitle(runs)}</span>
        <span className="truncate text-[11px] text-muted-foreground">{overallSummary(runs)}</span>

        {/* Defined buttons — a labeled red Stop pill (while running) + a
            matching neutral Close, both with border + subtle bg. */}
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {latestRun.runActive && (
            <button
              type="button"
              onClick={onStopRun}
              title="Stop test (saves tokens)"
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-red-500/25 bg-red-500/10 px-2 text-xs font-medium text-red-600 transition-colors hover:bg-red-500/20 dark:text-red-400"
            >
              <Square className="h-3 w-3 fill-current" />
              Stop
            </button>
          )}
          <button
            type="button"
            onClick={close}
            title="Close"
            className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-muted/40 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Run history — newest first; only when there's more than one run */}
      {runs.length > 1 && (
        <div className="flex shrink-0 gap-1.5 overflow-x-auto border-b border-border px-4 py-2">
          {runs
            .map((run, i) => ({ run, number: i + 1 }))
            .reverse()
            .map(({ run, number }) => {
              const active = run.taskId === viewedRun.taskId;
              const status = runStatus(run);
              return (
                <button
                  key={run.taskId}
                  type="button"
                  onClick={() => open(run.taskId)}
                  className={cn(
                    "flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] transition-colors",
                    active
                      ? "bg-accent font-medium text-foreground"
                      : "text-muted-foreground hover:bg-muted/60",
                  )}
                >
                  <span className={cn("h-1.5 w-1.5 rounded-full", DOT[status], run.runActive && "animate-pulse")} />
                  Run {number}
                </button>
              );
            })}
        </div>
      )}

      {/* Timeline */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 pt-4 pb-16">
        {viewedRun.steps.map((step, i) => (
          <StepRow key={step.toolUseId} step={step} isLast={i === viewedRun.steps.length - 1} />
        ))}
      </div>
    </div>
  );
}

/**
 * "Tests" reopen tab — an upside-down-U tab flush on the top-right edge of the
 * chat input box, shown only after the banner has been dismissed. Must be
 * rendered inside the (relative) chatbox container so it anchors to its top
 * edge; `bottom-full -mb-px` overlaps the border by 1px so there's no seam.
 */
export function PreviewTestReopenTab() {
  const runs = usePreviewTestStore((s) => s.runs);
  const isOpen = usePreviewTestStore((s) => s.isOpen);
  const dismissed = usePreviewTestStore((s) => s.dismissed);
  const open = usePreviewTestStore((s) => s.open);

  if (isOpen || !dismissed || runs.length === 0) return null;
  const latest = runs[runs.length - 1];
  const status = headlineVerdict(overallStatus(runs));

  return (
    <button
      type="button"
      onClick={() => open(latest.taskId)}
      title="Open Test Run"
      className="absolute bottom-full right-4 z-10 -mb-px flex items-center gap-1.5 rounded-t-lg border border-b-0 border-border/50 bg-card px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground animate-in slide-in-from-bottom-1 fade-in duration-200"
    >
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          DOT[status],
          status === "running" && "animate-pulse",
        )}
      />
      Tests
    </button>
  );
}
